import { Button } from '@/components/ui/button';
import { cn, randomId, wait } from '@/lib/utils';
import type { PaymentResult } from '@dash-lite/sdk';

import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    Elements,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';

import { loadStripe } from '@stripe/stripe-js';
import type {
    Stripe,
    StripeElementStyle,
} from '@stripe/stripe-js';

import { CreditCard } from 'lucide-react';

import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import type {
    FormEvent,
    ReactNode,
} from 'react';

import {
    Link,
    useNavigate,
} from 'react-router-dom';

import { Input } from '@/components/ui/field';

import {
    Alert,
    Card,
    CardBody,
    CardHeader,
    Skeleton,
} from '@/components/ui/primitives';

import { useDeviceSessionId } from '@/hooks/use-device-session';
import { useSession } from '@/hooks/use-session';
import { useSite } from '@/hooks/use-site';

import { dashlite } from '@/lib/dashlite';

import {
    fieldErrors,
    isApiError,
    presentError,
} from '@/lib/errors';

import {
    describeOffer,
    headlinePrice,
} from '@/lib/format';

import { paths } from '@/lib/paths';

/**
 * Tunnel de paiement complet.
 */
export function Checkout({
    metadata,
}: {
    metadata?: Record<string, string>;
}) {
    const { user } = useSession();
    const deviceSessionId = useDeviceSessionId();

    const [intent, setIntent] = useState<PaymentResult | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [creating, setCreating] = useState(true);

    const idempotencyKey = useRef(randomId());
    const paymentRequested = useRef(false);

    useEffect(() => {
        if (paymentRequested.current) {
            return;
        }

        paymentRequested.current = true;

        async function createPayment(): Promise<void> {
            setCreating(true);
            setError(null);

            try {
                const result = await dashlite.createPayment(
                    {
                        ...(user?.email
                            ? { email: user.email }
                            : {}),

                        ...(deviceSessionId
                            ? { device_session_id: deviceSessionId }
                            : {}),

                        ...(metadata &&
                        Object.keys(metadata).length > 0
                            ? { metadata }
                            : {}),
                    },
                    idempotencyKey.current,
                );

                setIntent(result);
            } catch (caught) {
                setError(caught);
            } finally {
                setCreating(false);
            }
        }

        void createPayment();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (creating) {
        return (
            <Card>
                <CardHeader
                    title="Préparation du paiement"
                    icon={
                        <CreditCard className="size-4" />
                    }
                />

                <CardBody className="space-y-3">
                    <Skeleton className="h-11 w-full" />
                    <Skeleton className="h-11 w-full" />
                    <Skeleton className="h-11 w-2/3" />
                </CardBody>
            </Card>
        );
    }

    if (error || !intent) {
        return (
            <PaymentSetupError error={error} />
        );
    }

    return (
        <StripeElementsShell
            intent={intent}
        />
    );
}

/**
 * Erreur pendant la création initiale du paiement.
 */
function PaymentSetupError({
    error,
}: {
    error: unknown;
}) {
    const presentation =
        presentError(error);

    return (
        <Alert
            tone="critical"
            title={presentation.message}
        >
            <p>
                {presentation.hint}
            </p>

            {isApiError(
                error,
                'offer_not_available',
            ) && (
                <p className="mt-2">
                    L&apos;offre n&apos;est pas
                    synchronisée avec le compte
                    Stripe du site pour cet
                    environnement.{' '}

                    <button
                        type="button"
                        onClick={() =>
                            window.location.reload()
                        }
                        className="underline"
                    >
                        Recharger la page
                    </button>
                </p>
            )}

            {isApiError(
                error,
                'account_exists',
            ) && (
                <p className="mt-2">
                    <Link
                        to={paths.login}
                        className="underline"
                    >
                        Se connecter
                    </Link>{' '}

                    ou{' '}

                    <Link
                        to={paths.forgotPassword}
                        className="underline"
                    >
                        définir un mot de passe
                    </Link>
                    .
                </p>
            )}

            {isApiError(
                error,
                'payment_flagged_fraud',
            ) && (
                <p className="mt-2">
                    Aucun objet n&apos;a été créé
                    chez Stripe et aucun montant
                    n&apos;a été débité : il
                    n&apos;y a rien à annuler.
                </p>
            )}
        </Alert>
    );
}

/**
 * Monte Stripe Elements avec la clé publishable
 * renvoyée par l'API.
 */
function StripeElementsShell({
    intent,
}: {
    intent: PaymentResult;
}) {
    const stripePromise =
        useMemo<Promise<Stripe | null>>(
            () =>
                loadStripe(
                    intent.publishable_key,
                ),
            [intent.publishable_key],
        );

    return (
        <Elements
            stripe={stripePromise}
            options={{
                clientSecret:
                    intent.client_secret,

                locale: 'fr',

                appearance: {
                    theme: 'stripe',

                    variables: {
                        colorPrimary:
                            '#3448a8',

                        colorText:
                            '#1e2233',

                        fontFamily:
                            'Inter, ui-sans-serif, system-ui, sans-serif',

                        borderRadius:
                            '8px',
                    },
                },
            }}
        >
            <CardPaymentForm
                intent={intent}
            />
        </Elements>
    );
}

/**
 * Apparence des champs Stripe.
 */
const CARD_FIELD_STYLE:
    StripeElementStyle = {
    base: {
        fontSize: '15px',

        fontFamily:
            'Inter, ui-sans-serif, system-ui, sans-serif',

        color: '#1e2233',

        '::placeholder': {
            color: '#8a8fa3',
        },
    },

    invalid: {
        color: '#c2410c',
        iconColor: '#c2410c',
    },
};

const CARD_BRANDS = [
    {
        key: 'visa',
        label: 'Visa',
    },
    {
        key: 'mastercard',
        label: 'Mastercard',
    },
    {
        key: 'amex',
        label: 'Amex',
    },
] as const;

type CardCompletion = {
    number: boolean;
    expiry: boolean;
    cvc: boolean;
};

/**
 * Conteneur d'un champ Stripe.
 */
function CardFieldShell({
    label,
    children,
    trailing,
    className,
}: {
    label: string;
    children: ReactNode;
    trailing?: ReactNode;
    className?: string;
}) {
    return (
        <div
            className={cn(
                'space-y-1.5',
                className,
            )}
        >
            <span className="text-ink block text-sm font-medium">
                {label}
            </span>

            <div className="border-line-strong focus-within:border-brand-500 focus-within:ring-brand-500/20 flex items-center gap-3 rounded-lg border bg-white px-3.5 py-3.5 transition-colors focus-within:ring-2">

                <div className="min-w-0 flex-1">
                    {children}
                </div>

                {trailing}
            </div>
        </div>
    );
}

/**
 * Formulaire de paiement.
 */
function CardPaymentForm({
    intent,
}: {
    intent: PaymentResult;
}) {
    const stripe = useStripe();
    const elements = useElements();

    const navigate = useNavigate();

    const site = useSite();

    const {
        user,
        adoptAuthResult,
    } = useSession();

    const [email, setEmail] =
        useState(
            user?.email ?? '',
        );

    const [
        submitting,
        setSubmitting,
    ] = useState(false);

    const [
        stage,
        setStage,
    ] = useState<
        | 'idle'
        | 'confirming'
        | 'claiming'
    >('idle');

    const [
        error,
        setError,
    ] = useState<unknown>(null);

    const [
        cardError,
        setCardError,
    ] = useState<
        string | null
    >(null);

    const [
        cardBrand,
        setCardBrand,
    ] = useState('unknown');

    const [
        completion,
        setCompletion,
    ] =
        useState<CardCompletion>({
            number: false,
            expiry: false,
            cvc: false,
        });

    const fields =
        fieldErrors(error);

    const cardComplete =
        completion.number &&
        completion.expiry &&
        completion.cvc;

    async function handleSubmit(
        event: FormEvent,
    ): Promise<void> {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        const card =
            elements.getElement(
                CardNumberElement,
            );

        if (!card) {
            return;
        }

        setSubmitting(true);
        setError(null);
        setCardError(null);

        try {
            setStage('confirming');

            const confirmation =
                await stripe.confirmCardPayment(
                    intent.client_secret,
                    {
                        payment_method: {
                            card,

                            billing_details:
                                email
                                    ? {
                                          email,
                                      }
                                    : undefined,
                        },
                    },
                );

            if (
                confirmation.error
            ) {
                setCardError(
                    confirmation.error
                        .message ??
                        'La banque a refusé la transaction.',
                );

                return;
            }

            setStage('claiming');

            const auth =
                await claimWithRetry(
                    intent.payment_id,
                    email,
                );

            adoptAuthResult(auth);

            navigate(
                paths.checkoutSuccess,
                {
                    replace: true,
                },
            );
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
            setStage('idle');
        }
    }

    return (
        <div className="space-y-6">

            <Card className="overflow-hidden">

                {/* HEADER DU PAIEMENT */}
                <div className="w-full">
                    <div className="overflow-hidden">

                        <div
                            className="px-4 py-5 text-center"
                            style={{
                                fontFamily:
                                    "'Roboto', sans-serif",
                            }}
                        >
                            <h2
                                className="my-[6px] font-black tracking-[-0.02em]"
                                style={{
                                    fontSize:
                                        'clamp(22px, 3.6vw, 34px)',

                                    color:
                                        '#0f172a',
                                }}
                            >
                                Paiement en ligne sécurisé
                            </h2>

                            <p
                                style={{
                                    fontSize:
                                        'clamp(14px, 2.2vw, 19px)',

                                    color:
                                        '#6b7280',
                                }}
                            >
                                Montant à régler:{' '}

                                <span
                                    style={{
                                        fontWeight:
                                            900,

                                        color:
                                            '#3E92CC',
                                    }}
                                >
                                    {site.offer
                                        ? headlinePrice(
                                              site.offer,
                                          )
                                        : ''}
                                </span>
                            </p>
                        </div>
                    </div>
                </div>

                <CardBody>
                    <form
                        onSubmit={
                            handleSubmit
                        }
                        className="space-y-5"
                        noValidate
                    >
                        {/* EMAIL */}
                        <Input
                            label="Email"
                            type="email"
                            required
                            autoComplete="email"
                            placeholder="vous@exemple.fr"
                            value={email}
                            error={
                                fields.email
                            }
                            onChange={(
                                event,
                            ) =>
                                setEmail(
                                    event.target
                                        .value,
                                )
                            }
                            hint=""
                        />

                        {/* CARTE */}
                        <div className="space-y-4">

                            <CardFieldShell
                                label="Numéro de carte"
                                trailing={
                                    <span
                                        aria-hidden
                                        className="flex shrink-0 items-center gap-1.5"
                                    >
                                        {CARD_BRANDS.map(
                                            (
                                                network,
                                            ) => (
                                                <span
                                                    key={
                                                        network.key
                                                    }
                                                    className={cn(
                                                        'border-line rounded border px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase transition-colors',

                                                        cardBrand ===
                                                            network.key
                                                            ? 'border-brand-300 bg-brand-50 text-brand-700'
                                                            : 'text-ink-muted/70',
                                                    )}
                                                >
                                                    {
                                                        network.label
                                                    }
                                                </span>
                                            ),
                                        )}
                                    </span>
                                }
                            >
                                <CardNumberElement
                                    options={{
                                        style:
                                            CARD_FIELD_STYLE,

                                        showIcon:
                                            false,
                                    }}
                                    onChange={(
                                        event,
                                    ) => {
                                        setCardBrand(
                                            event.brand,
                                        );

                                        setCompletion(
                                            (
                                                previous,
                                            ) => ({
                                                ...previous,

                                                number:
                                                    event.complete,
                                            }),
                                        );

                                        setCardError(
                                            event
                                                .error
                                                ?.message ??
                                                null,
                                        );
                                    }}
                                />
                            </CardFieldShell>

                            <div className="grid gap-4 sm:grid-cols-2">

                                <CardFieldShell label="Expiration">
                                    <CardExpiryElement
                                        options={{
                                            style:
                                                CARD_FIELD_STYLE,
                                        }}
                                        onChange={(
                                            event,
                                        ) => {
                                            setCompletion(
                                                (
                                                    previous,
                                                ) => ({
                                                    ...previous,

                                                    expiry:
                                                        event.complete,
                                                }),
                                            );

                                            setCardError(
                                                event
                                                    .error
                                                    ?.message ??
                                                    null,
                                            );
                                        }}
                                    />
                                </CardFieldShell>

                                <CardFieldShell label="CVC">
                                    <CardCvcElement
                                        options={{
                                            style:
                                                CARD_FIELD_STYLE,
                                        }}
                                        onChange={(
                                            event,
                                        ) => {
                                            setCompletion(
                                                (
                                                    previous,
                                                ) => ({
                                                    ...previous,

                                                    cvc:
                                                        event.complete,
                                                }),
                                            );

                                            setCardError(
                                                event
                                                    .error
                                                    ?.message ??
                                                    null,
                                            );
                                        }}
                                    />
                                </CardFieldShell>

                            </div>

                            {cardError && (
                                <p
                                    role="alert"
                                    className="text-critical text-xs"
                                >
                                    {cardError}
                                </p>
                            )}

                            {site.environment ===
                                'test' && (
                                <p className="text-ink-muted text-xs">
                                    Environnement
                                    de test —
                                    utilisez la
                                    carte{' '}

                                    <span className="font-mono">
                                        4242 4242
                                        4242 4242
                                    </span>
                                    , n&apos;importe
                                    quelle date
                                    future et
                                    n&apos;importe
                                    quel
                                    cryptogramme.
                                </p>
                            )}
                        </div>

                        {/* ERREUR API */}
                        {error !== null &&
                            Object.keys(
                                fields,
                            ).length ===
                                0 && (
                                <PaymentSubmitError
                                    error={
                                        error
                                    }
                                />
                            )}

                        <RecurringCommitmentNotice />

                        {/* BOUTON PAYER */}
                        <Button
                            type="submit"
                            size="lg"
                            style={{
                                display:
                                    'flex',

                                width:
                                    'calc(100% - 48px)',

                                height:
                                    '57px',

                                marginLeft:
                                    'auto',

                                marginRight:
                                    'auto',

                                padding:
                                    '0 24px',

                                alignItems:
                                    'center',

                                justifyContent:
                                    'center',

                                gap:
                                    '8px',

                                border:
                                    'none',

                                borderRadius:
                                    '9999px',

                                background:
                                    'linear-gradient(135deg, #3E92CC 0%, #1D3557 100%)',

                                color:
                                    '#ffffff',

                                fontSize:
                                    '1.1rem',

                                fontWeight:
                                    800,

                                lineHeight:
                                    1.2,

                                letterSpacing:
                                    '0.1px',

                                textAlign:
                                    'center',

                                boxShadow:
                                    '0 12px 28px rgba(10, 36, 99, 0.22)',
                            }}
                            className="
                                transition-all
                                duration-150
                                hover:-translate-y-[1px]
                                hover:brightness-105
                                active:translate-y-0
                                disabled:cursor-not-allowed
                                disabled:opacity-100
                            "
                            loading={
                                submitting
                            }
                            disabled={
                                !stripe ||
                                !cardComplete ||
                                email.trim() ===
                                    ''
                            }
                            data-dashlite="payment-submit"
                        >
                            {stage ===
                            'claiming'
                                ? 'Ouverture de votre accès…'

                                : stage ===
                                    'confirming'
                                  ? 'Confirmation du paiement…'

                                  : site.offer
                                    ? `Valider et payer ${headlinePrice(
                                          site.offer,
                                      )}`

                                    : 'Payer'}
                        </Button>

                        {/* MENTIONS */}
                        <div className="text-ink-muted space-y-1.5">

                            <div className="mt-1 mb-5 rounded-lg border border-border bg-card px-5 py-3 text-justify text-[9px] leading-relaxed text-muted-foreground">
                                Helvedoc est une
                                plateforme privée
                                d&apos;information
                                légale et
                                financière,
                                totalement
                                indépendante de
                                tout organisme
                                public et du
                                Registre National
                                du Commerce et des
                                Sociétés (RCS).

                                Helvedoc facilite
                                l&apos;accès à ses
                                services pour les
                                professionnels.

                                L&apos;utilisation
                                de notre portail
                                ne remplace pas
                                les documents
                                officiels délivrés
                                par les greffes
                                des tribunaux de
                                commerce.

                                L&apos;accès
                                illimité via notre
                                forfait pro à
                                68 € par mois
                                inclut une offre
                                d&apos;essai à
                                1,60 € rétractable
                                dans les
                                48 heures avant
                                le premier
                                prélèvement de
                                l&apos;abonnement.

                                Par la suite,
                                celui-ci est
                                résiliable sans
                                frais à tout
                                moment depuis
                                votre espace
                                client, la demande
                                prenant effet
                                après un préavis
                                obligatoire
                                d&apos;un mois,
                                chaque période
                                entamée étant
                                intégralement due.
                            </div>

                        </div>

                    </form>
                </CardBody>
            </Card>

        </div>
    );
}

/**
 * Informations sur l'abonnement récurrent.
 */
function RecurringCommitmentNotice() {
    const site = useSite();
    const offer = site.offer;

    if (
        !offer ||
        offer.type === 'one_time'
    ) {
        return null;
    }

    return (
        <p className="text-ink-soft text-xs leading-relaxed">

            En validant, vous souscrivez à :{' '}

            <strong className="text-ink font-medium">
                {describeOffer(offer)}
            </strong>
            .

            {' '}
            Le renouvellement est automatique jusqu&apos;à résiliation,{' '}

            <Link
                to={
                    paths.cancelSubscription
                }
                className="underline"
            >
                possible en ligne
            </Link>{' '}

            à tout moment et sans justification.

        </p>
    );
}

/**
 * Erreur survenue après confirmation Stripe.
 */
function PaymentSubmitError({
    error,
}: {
    error: unknown;
}) {
    const presentation =
        presentError(error);

    return (
        <Alert
            tone="critical"
            title={
                presentation.message
            }
        >
            <p>
                {presentation.hint}
            </p>

            {isApiError(
                error,
                'account_exists',
            ) && (
                <p className="mt-2">
                    <Link
                        to={paths.login}
                        className="underline"
                    >
                        Connectez-vous
                    </Link>{' '}

                    pour rattacher cet
                    achat à votre compte
                    existant.
                </p>
            )}
        </Alert>
    );
}

/**
 * Réclame le paiement avec plusieurs tentatives.
 */
async function claimWithRetry(
    paymentId: string,
    email: string,
    attempts = 3,
) {
    let lastError: unknown;

    for (
        let attempt = 0;
        attempt < attempts;
        attempt++
    ) {
        try {
            return await dashlite.claimPayment(
                {
                    payment_id:
                        paymentId,

                    ...(email
                        ? { email }
                        : {}),
                },
            );
        } catch (error) {
            lastError = error;

            if (
                !isApiError(
                    error,
                    'validation_failed',
                ) ||
                attempt ===
                    attempts - 1
            ) {
                throw error;
            }

            await wait(
                1200 *
                    (attempt + 1),
            );
        }
    }

    throw lastError;
}